// lib/online-orders/substitutionPrefs.ts
//
// Preferencia del cliente para «si algo no está disponible» (spec
// docs/superpowers/specs/2026-09-28-sustituciones-pedidos-online-design.md §4).
// Módulo NORMAL (sin "use server"): lo importan el cliente (selector), la ruta
// de escritura y la action pública. Función pura: nada de I/O aquí.
//
// Regla de oro (§4.3): NUNCA se rechaza una orden por esto — la orden ya está
// cobrada cuando llega a /api/online-orders. Lo inválido se degrada y se avisa.

export const SUBSTITUTION_PREFS = ["similar", "refund", "contact"] as const
export type SubstitutionPref = (typeof SUBSTITUTION_PREFS)[number]

export function isSubstitutionPref(v: unknown): v is SubstitutionPref {
  return typeof v === "string" && (SUBSTITUTION_PREFS as readonly string[]).includes(v)
}

export type SubstitutionWarningReason =
  | "contact_needs_sms"
  | "not_found"
  | "inactive"
  | "sold_out"
  | "other_category"
  | "more_expensive"
  | "same_item"
  | "pref_not_saved"

/** `cart_index = -1` = la preferencia de la orden; ≥ 0 = esa línea. */
export type SubstitutionWarning = { cart_index: number; reason: SubstitutionWarningReason }

export type SubstitutionLineInput = {
  item_id: string
  substitution_pref?: SubstitutionPref
  substitution_item_id?: string
}

/** Lo mínimo de `items` para decidir si un plan B vale. Precio CARD crudo (items.price). */
export type SubstitutionItemFacts = {
  category_id: string | null
  price: number
  is_active: boolean | null
  is_available: boolean | null
}

export type ResolvedSubstitutionPrefs = {
  orderPref: SubstitutionPref
  lines: Array<{ substitution_pref: SubstitutionPref | null; substitution_item_id: string | null }>
  warnings: SubstitutionWarning[]
}

function candidateProblem(
  lineItemId: string,
  subId: string,
  originals: ReadonlyMap<string, SubstitutionItemFacts>,
  candidates: ReadonlyMap<string, SubstitutionItemFacts>,
): SubstitutionWarningReason | null {
  if (subId === lineItemId) return "same_item"
  const cand = candidates.get(subId)
  if (!cand) return "not_found"
  // NULL no es agotado: misma regla que lib/online-orders/pricing.ts (`=== false`).
  if (cand.is_active === false) return "inactive"
  if (cand.is_available === false) return "sold_out"
  const orig = originals.get(lineItemId)
  if (!orig || cand.category_id === null || cand.category_id !== orig.category_id) return "other_category"
  if (cand.price > orig.price) return "more_expensive"
  return null
}

export function resolveSubstitutionPrefs(args: {
  enabled: boolean
  orderPref: SubstitutionPref | undefined
  smsOptIn: boolean
  items: SubstitutionLineInput[]
  originals: ReadonlyMap<string, SubstitutionItemFacts>
  candidates: ReadonlyMap<string, SubstitutionItemFacts>
}): ResolvedSubstitutionPrefs {
  const empty = { substitution_pref: null, substitution_item_id: null }
  if (!args.enabled) {
    return { orderPref: "similar", lines: args.items.map(() => ({ ...empty })), warnings: [] }
  }

  const warnings: SubstitutionWarning[] = []
  let orderPref: SubstitutionPref = args.orderPref ?? "similar"
  if (orderPref === "contact" && !args.smsOptIn) {
    orderPref = "similar"
    warnings.push({ cart_index: -1, reason: "contact_needs_sms" })
  }

  const lines = args.items.map((line, idx) => {
    let pref: SubstitutionPref | null = line.substitution_pref ?? null
    if (pref === "contact" && !args.smsOptIn) {
      pref = null
      warnings.push({ cart_index: idx, reason: "contact_needs_sms" })
    }
    let subId: string | null = line.substitution_item_id ?? null
    if (subId) {
      const problem = candidateProblem(line.item_id, subId, args.originals, args.candidates)
      if (problem) {
        subId = null
        warnings.push({ cart_index: idx, reason: problem })
      }
    }
    return { substitution_pref: pref, substitution_item_id: subId }
  })

  return { orderPref, lines, warnings }
}
